import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { create } from 'zustand';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import type { InvitationDto } from '@/types/dtos/invitations/invitation.dto.ts';

interface EventState {
  // PLAN mode documents
  eventPlan?: EventDto;
  eventFlowsPlan?: FlowDto[];
  eventSessionsPlan?: SessionDto[];
  // ORGANIZE mode documents
  eventOrganize?: EventDto;
  eventFlowsOrganize?: FlowDto[];
  eventSessionsOrganize?: SessionDto[];
  // Computed/current documents (backward compatibility)
  event?: EventDto;
  eventFlows?: FlowDto[];
  eventSessions?: SessionDto[];
  // Other data
  contributors?: ContributorDto[];
  invitations?: InvitationDto[];
  selectedSession?: SessionDto;
  // Setters for PLAN mode
  setEventPlan: (change: EventDto) => void;
  setEventFlowsPlan: (change: FlowDto[]) => void;
  setEventSessionsPlan: (change: SessionDto[]) => void;
  // Setters for ORGANIZE mode
  setEventOrganize: (change: EventDto) => void;
  setEventFlowsOrganize: (change: FlowDto[]) => void;
  setEventSessionsOrganize: (change: SessionDto[]) => void;
  // Legacy setters (for backward compatibility, will compute based on mode)
  setEvent: (change: EventDto) => void;
  setEventFlows: (change: FlowDto[]) => void;
  setEventSessions: (change: SessionDto[]) => void;
  setEventContributors: (change: ContributorDto[]) => void;
  setEventInvitations: (change: InvitationDto[]) => void;
  setSelectedSession: (session: SessionDto | undefined) => void;
  clearEventData: () => void;
}

export const useEventStore = create<EventState>()((set, get) => ({
  eventPlan: undefined,
  eventFlowsPlan: undefined,
  eventSessionsPlan: undefined,
  eventOrganize: undefined,
  eventFlowsOrganize: undefined,
  eventSessionsOrganize: undefined,
  event: undefined,
  eventFlows: undefined,
  eventSessions: undefined,
  contributors: undefined,
  invitations: undefined,
  selectedSession: undefined,
  // Setters for PLAN mode
  setEventPlan: (change) =>
    set(() => {
      // Also update computed event if PLAN is available (prefer PLAN over ORGANIZE)
      return {
        eventPlan: change,
        event: change, // PLAN takes precedence
      };
    }),
  setEventFlowsPlan: (change) =>
    set(() => {
      return {
        eventFlowsPlan: change,
        eventFlows: change, // PLAN takes precedence
      };
    }),
  setEventSessionsPlan: (change) =>
    set(() => {
      return {
        eventSessionsPlan: change,
        eventSessions: change, // PLAN takes precedence
      };
    }),
  // Setters for ORGANIZE mode
  setEventOrganize: (change) =>
    set(() => {
      const state = get();
      // Only set computed event if PLAN doesn't exist
      return {
        eventOrganize: change,
        event: state.eventPlan || change, // Use PLAN if available, else ORGANIZE
      };
    }),
  setEventFlowsOrganize: (change) =>
    set(() => {
      const state = get();
      return {
        eventFlowsOrganize: change,
        eventFlows: state.eventFlowsPlan || change, // Use PLAN if available, else ORGANIZE
      };
    }),
  setEventSessionsOrganize: (change) =>
    set(() => {
      const state = get();
      return {
        eventSessionsOrganize: change,
        eventSessions: state.eventSessionsPlan || change, // Use PLAN if available, else ORGANIZE
      };
    }),
  // Legacy setters (for backward compatibility)
  setEvent: (change) =>
    set(() => {
      // Store based on mode field
      if (change.mode === 'PLAN') {
        return { eventPlan: change, event: change };
      } else {
        const state = get();
        return {
          eventOrganize: change,
          event: state.eventPlan || change, // Use PLAN if available, else ORGANIZE
        };
      }
    }),
  setEventFlows: (change) =>
    set(() => {
      // Determine mode from first flow if available
      const firstFlow = change[0];
      if (firstFlow?.mode === 'PLAN') {
        return { eventFlowsPlan: change, eventFlows: change };
      } else {
        const state = get();
        return {
          eventFlowsOrganize: change,
          eventFlows: state.eventFlowsPlan || change,
        };
      }
    }),
  setEventSessions: (change) =>
    set(() => {
      // Determine mode from first session if available
      const firstSession = change[0];
      if (firstSession?.mode === 'PLAN') {
        return { eventSessionsPlan: change, eventSessions: change };
      } else {
        const state = get();
        return {
          eventSessionsOrganize: change,
          eventSessions: state.eventSessionsPlan || change,
        };
      }
    }),
  setEventContributors: (change) => set(() => ({ contributors: change })),
  setEventInvitations: (change) => set(() => ({ invitations: change })),
  setSelectedSession: (session) => set(() => ({ selectedSession: session })),
  clearEventData: () =>
    set(() => ({
      eventPlan: undefined,
      eventFlowsPlan: undefined,
      eventSessionsPlan: undefined,
      eventOrganize: undefined,
      eventFlowsOrganize: undefined,
      eventSessionsOrganize: undefined,
      event: undefined,
      eventFlows: undefined,
      eventSessions: undefined,
      contributors: undefined,
      invitations: undefined,
      selectedSession: undefined,
    })),
}));
